import React from "react";

import { ButtonStandard } from "@performyard/py-components";
import { SurveyAnswerJson, SurveyQuestionJson } from "@performyard/service-contracts";

import {
	InstructionsQuestion,
	MultipleChoiceQuestion,
	MultipleSelectQuestion,
	RatingScaleQuestion,
	TextQuestion,
} from "./Questions";

import { useActions } from "../store/store";

interface QuestionsFormProps {
	questions: SurveyQuestionJson[];
	onSubmit: (answers: Record<string, SurveyAnswerJson["value"]>) => void;
}

export const QuestionsForm = ({ questions, onSubmit }: QuestionsFormProps) => {
	const actions = useActions();
	const renderMultipleChoice = (question: SurveyQuestionJson) => {
		if (!question.options || !Array.isArray(question.options)) {
			return null;
		}
		switch (question.type) {
			case "multiple-choice":
				return <MultipleChoiceQuestion question={question} />;
			case "multiple-select":
				return <MultipleSelectQuestion question={question} />;
		}
	};

	const renderQuestion = (question: SurveyQuestionJson) => {
		switch (question.type) {
			case "instructions":
				return <InstructionsQuestion question={question} />;
			case "text":
				return <TextQuestion question={question} />;
			case "multiple-choice":
			case "multiple-select":
				return renderMultipleChoice(question);
			case "rating-scale":
				if (!question.options || !Array.isArray(question.options)) {
					return null;
				}
				return <RatingScaleQuestion question={question} />;
			default:
				return null;
		}
	};

	const renderQuestionWithId = (question: SurveyQuestionJson) => {
		return (
			<span key={question._id} data-testid={`question-${question._id}`}>
				{renderQuestion(question)}
			</span>
		);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		actions.validateAnswers((answers) => {
			onSubmit(answers);
		});
	};

	return (
		<form data-testid="questions-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
			{questions.map(renderQuestionWithId)}
			<ButtonStandard data-testid="submit-answers-button" type="submit" color="primary" size="large">
				Submit Answers
			</ButtonStandard>
		</form>
	);
};
